import React, { useState, useRef } from 'react';
import ApiPost from '../../../../../../api-controller-mod/api-serv/post-api/postApi';
import { FooterData, FooterContent, MediaSaveCallback } from './types';
import { LabelRow, SplitRow, DraftTextField } from './primitives';
import ImageUploadField from './imageUploadField';

interface FooterEditorProps {
  data:               FooterData;
  onChange:           (v: FooterData) => void;
  onMediaClick?:      (onSave: MediaSaveCallback) => void;
  userId:             string;
  registerMediaUsage: (mediaUuid: string) => void;
}

const FooterEditor: React.FC<FooterEditorProps> = ({
  data, onChange, onMediaClick, userId, registerMediaUsage,
}) => {
  const layout  = data.cta[0]?.layout;
  const content = layout?.content[0];
  const orig    = useRef(content);

  const [footerLeftVisible,    setFooterLeftVisible]    = useState(content?.footerLeft.visible    ?? true);
  const [footerRightVisible,   setFooterRightVisible]   = useState(content?.footerRight.visible   ?? true);
  const [headerSectionVisible, setHeaderSectionVisible] = useState(content?.headerSection.visible ?? true);

  if (!content) return null;

  const setContent = (p: Partial<FooterContent>) =>
    onChange({ ...data, cta: [{ layout: { ...layout, content: [{ ...content, ...p }] } }] });

  const setLeftTitle   = (title: string) => setContent({ footerLeft:    { ...content.footerLeft,    title } });
  const setHeaderTitle = (title: string) => setContent({ headerSection: { ...content.headerSection, title } });
  const setRightItem   = (idx: number, title: string) => {
    const lists = content.footerRight.lists.map((l, i) => i === idx ? { ...l, title } : l);
    setContent({ footerRight: { ...content.footerRight, lists } });
  };

  return (
    <>
      {/* ── Footer Left ── */}
      <LabelRow
        label="Copyright text"
        toggle={{
          checked:  footerLeftVisible,
          onChange: v => {
            setFooterLeftVisible(v);
            setContent({ footerLeft: { ...content.footerLeft, visible: v } });
          },
        }}
      />
      <SplitRow currentText={orig.current?.footerLeft.title ?? ''}>
        <DraftTextField placeholder="© 2025 SCL. All rights reserved." onChange={v => setLeftTitle(v)} />
      </SplitRow>

      {/* ── Footer Right ── */}
      <LabelRow
        label="Footer links"
        toggle={{
          checked:  footerRightVisible,
          onChange: v => {
            setFooterRightVisible(v);
            setContent({ footerRight: { ...content.footerRight, visible: v } });
          },
        }}
      />
      {orig.current?.footerRight.lists.map((origLink, idx) => (
        <React.Fragment key={origLink.id}>
          <LabelRow label={`Footer link ${idx + 1}`} />
          <SplitRow currentText={origLink.title}>
            <DraftTextField placeholder="Link title" onChange={v => setRightItem(idx, v)} />
          </SplitRow>
        </React.Fragment>
      ))}

      {/* ── Header Section ── */}
      <LabelRow
        label="Brand title"
        toggle={{
          checked:  headerSectionVisible,
          onChange: v => {
            setHeaderSectionVisible(v);
            setContent({ headerSection: { ...content.headerSection, visible: v } });
          },
        }}
      />
      <SplitRow currentText={orig.current?.headerSection.title ?? ''}>
        <DraftTextField placeholder="Brand name" onChange={v => setHeaderTitle(v)} />
      </SplitRow>

      <ImageUploadField
        label="Logo"
        currentSrc={content.headerSection.imgLogo || undefined}
        defaultName="LOGO"
        onFile={async file => {
          try {
            const formData = new FormData();
            formData.append('file',        file);
            formData.append('type',        'IMAGE');
            formData.append('filename',    file.name);
            formData.append('alt',         '');
            formData.append('description', '');

            const res           = await ApiPost(`/api/v1/media/upload?userUuid=${userId}`, formData);
            const uploadedUrl  = (res as any)?.data?.data?.url  as string | undefined;
            const uploadedUuid = (res as any)?.data?.data?.uuid as string | undefined;

            if (uploadedUrl) {
              setContent({ headerSection: { ...content.headerSection, imgLogo: uploadedUrl } });
              if (uploadedUuid) registerMediaUsage(uploadedUuid);
              return uploadedUrl;
            }
          } catch (err) {
            console.error('[FooterLogoUpload] upload failed:', err);
          }
          return undefined;
        }}
        onMediaClick={
          onMediaClick
            ? () => onMediaClick(item => {
                if (item) setContent({ headerSection: { ...content.headerSection, imgLogo: item.src } });
              })
            : undefined
        }
      />
    </>
  );
};

export default FooterEditor;